import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  Gamepad2, Target, Clock, TrendingUp, 
  Zap, Trophy, Brain, ChevronRight 
} from 'lucide-react';
import { format } from 'date-fns';

const GAME_ICONS: Record<string, string> = {
  'League of Legends': '🎮',
  'CS2': '🔫',
  'Dota 2': '⚔️',
  'Valorant': '🎯',
  'Rocket League': '🚗',
  'Overwatch 2': '🛡️',
};

const GAME_COLORS: Record<string, string> = {
  'League of Legends': 'from-blue-500/20 to-cyan-500/20 border-blue-500/30',
  'CS2': 'from-orange-500/20 to-yellow-500/20 border-orange-500/30',
  'Dota 2': 'from-red-500/20 to-pink-500/20 border-red-500/30',
  'Valorant': 'from-pink-500/20 to-purple-500/20 border-pink-500/30',
};

export function EsportsPredictions() {
  const { data: predictions, isLoading } = useQuery({
    queryKey: ['esports-predictions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('esports_predictions')
        .select('*')
        .order('match_date', { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const games = [...new Set(predictions?.map((p) => p.game) || [])];

  const getConfidenceColor = (score: number) => {
    if (score >= 70) return 'text-green-500';
    if (score >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="space-y-6">
      <Card className="glass-card overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600/20 via-pink-500/20 to-blue-500/20 p-6">
          <div className="flex items-center gap-3">
            <Gamepad2 className="h-8 w-8 text-primary" />
            <div>
              <h2 className="text-2xl font-bold">Esports AI Predictions</h2>
              <p className="text-muted-foreground">
                AI-powered insights for competitive gaming
              </p>
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <Badge variant="outline" className="bg-background/50">
              <Brain className="h-3 w-3 mr-1" />
              ML-Powered
            </Badge>
            <Badge variant="outline" className="bg-background/50">
              <Zap className="h-3 w-3 mr-1" />
              Real-time Odds
            </Badge>
            <Badge variant="outline" className="bg-background/50">
              <Trophy className="h-3 w-3 mr-1" />
              Pro Analysis
            </Badge>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5 h-auto">
          <TabsTrigger value="all" className="py-2">All Games</TabsTrigger>
          {games.slice(0, 4).map((game) => (
            <TabsTrigger key={game} value={game} className="py-2">
              {GAME_ICONS[game]} {game.split(' ')[0]}
            </TabsTrigger>
          ))}
        </TabsList>

        {['all', ...games].map((tab) => (
          <TabsContent key={tab} value={tab} className="space-y-4">
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading predictions...</div>
            ) : (
              predictions
                ?.filter((p) => tab === 'all' || p.game === tab)
                .map((prediction) => (
                  <Card
                    key={prediction.id}
                    className={`glass-card border bg-gradient-to-r ${GAME_COLORS[prediction.game] || 'from-gray-500/20 to-gray-600/20 border-gray-500/30'}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl">{GAME_ICONS[prediction.game]}</span>
                            <Badge variant="secondary">{prediction.tournament}</Badge>
                            <Badge variant="outline">
                              <Clock className="h-3 w-3 mr-1" />
                              {format(new Date(prediction.match_date), 'MMM d, HH:mm')}
                            </Badge>
                          </div>

                          <div className="flex items-center justify-between py-4">
                            <div className="text-center flex-1">
                              <p className="font-bold text-lg">{prediction.team_a}</p>
                              <p className="text-2xl font-bold text-primary">
                                {prediction.odds_team_a?.toFixed(2)}
                              </p>
                            </div>

                            <div className="px-4">
                              <Badge
                                variant="secondary"
                                className="bg-primary/20 text-primary text-lg px-4 py-1"
                              >
                                VS
                              </Badge>
                            </div>

                            <div className="text-center flex-1">
                              <p className="font-bold text-lg">{prediction.team_b}</p>
                              <p className="text-2xl font-bold text-primary">
                                {prediction.odds_team_b?.toFixed(2)}
                              </p>
                            </div>
                          </div>

                          <div className="bg-background/50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Target className="h-4 w-4 text-primary" />
                              <span className="font-medium">AI Prediction:</span>
                              <span className="text-primary font-bold">
                                {prediction.predicted_winner}
                              </span>
                              <span className={`ml-auto font-bold ${getConfidenceColor(prediction.confidence_score || 0)}`}>
                                {prediction.confidence_score}% confident
                              </span>
                            </div>
                            <Progress value={prediction.confidence_score || 0} className="h-2" />
                          </div>
                        </div>

                        <div className="lg:w-80 space-y-3">
                          <div className="bg-background/50 p-3 rounded-lg">
                            <p className="text-sm text-muted-foreground flex items-start gap-2">
                              <Brain className="h-4 w-4 mt-0.5 shrink-0" />
                              {prediction.ai_analysis}
                            </p>
                          </div>
                          <Button className="w-full">
                            <TrendingUp className="h-4 w-4 mr-2" />
                            View Full Analysis
                            <ChevronRight className="h-4 w-4 ml-auto" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
